// Per-application notification template fields on the `applications` table.
// Placeholder metadata mirrors what the backend substitution code actually replaces
// (clients.service.js emailClientInvite + SSG OTP repository + Mobex Cloud Lambda for SMS).
// TODO: once the dedicated GET /applications/notification-templates endpoint lands,
// this metadata will come from the API response instead.

export const FLOW_GROUPS = [
    {
        key: 'welcome',
        label: 'Welcome',
        description: 'Sent when a member account is created.',
        emailField: 'msg_email_welcome',
        smsField: 'msg_sms_welcome',
    },
    {
        key: 'verify_passcode',
        label: 'Verify Passcode',
        description: 'One-time passcode sent to verify a member during sign-up / invite.',
        emailField: 'msg_email_verify_passcode',
        smsField: 'msg_sms_verify_passcode',
    },
    {
        key: 'forget_password',
        label: 'Forgot Password',
        description: 'One-time passcode sent when a member resets their password.',
        emailField: 'msg_email_forget_password',
        smsField: 'msg_sms_forget_password',
    },
    {
        key: 'login_passcode',
        label: 'Login Passcode',
        description: 'One-time passcode sent when a member signs in.',
        emailField: 'msg_email_login_passcode',
        smsField: 'msg_sms_login_passcode',
    },
];

export const TEMPLATE_FIELDS = FLOW_GROUPS.flatMap((group) => [group.emailField, group.smsField]);

const OTP_PLACEHOLDERS = [
    { token: 'otp', description: 'The one-time passcode', required: true },
    { token: 'app_name', description: 'Application display name', required: false },
];

export const PLACEHOLDERS = {
    msg_email_welcome: [
        { token: 'first_name', description: "Member's first name", required: false },
        { token: 'app_name', description: 'Application display name', required: false },
        { token: 'username', description: "Member's login username", required: true },
        { token: 'password_block', description: 'Password paragraph (only rendered for internal test accounts)', required: false },
        // Legacy aliases still substituted by the backend for older templates
        { token: 'firstName', description: "Member's first name (legacy alias)", required: false, legacy: true },
        { token: 'application', description: 'Application name (legacy alias)', required: false, legacy: true },
        { token: 'companyName', description: 'Company name (legacy alias)', required: false, legacy: true },
        { token: 'email', description: "Member's email (legacy alias)", required: false, legacy: true },
        { token: 'password', description: "Member's password (legacy alias)", required: false, legacy: true },
    ],
    msg_sms_welcome: [
        { token: 'android_url', description: 'Google Play Store link', required: false },
        { token: 'ios_url', description: 'Apple App Store link', required: false },
    ],
    msg_email_verify_passcode: OTP_PLACEHOLDERS,
    msg_sms_verify_passcode: OTP_PLACEHOLDERS,
    msg_email_forget_password: OTP_PLACEHOLDERS,
    msg_sms_forget_password: OTP_PLACEHOLDERS,
    msg_email_login_passcode: OTP_PLACEHOLDERS,
    msg_sms_login_passcode: OTP_PLACEHOLDERS,
};

export const TOKEN_REGEX = /\{\{\s*([a-zA-Z_]+)\s*\}\}/g;

export function extractTokens(text) {
    const tokens = new Set();
    let match;
    TOKEN_REGEX.lastIndex = 0;
    while ((match = TOKEN_REGEX.exec(text || '')) !== null) {
        tokens.add(match[1]);
    }
    return tokens;
}

// Required placeholders missing from the template text — blocks saving
export function getMissingRequiredTokens(fieldName, text) {
    const used = extractTokens(text);
    return (PLACEHOLDERS[fieldName] || [])
        .filter((placeholder) => placeholder.required && !used.has(placeholder.token))
        .map((placeholder) => placeholder.token);
}

// Sample values substituted into the live preview
export function getSampleValues(application) {
    const appName = application?.name || 'MobexHealth';

    return {
        otp: '123456',
        app_name: appName,
        first_name: 'Jane',
        username: 'jane.doe@example.com',
        password_block: '<p>This is your password: TempPass123!</p>',
        android_url: application?.url_android_shortened || application?.url_android || 'https://play.google.com/store/apps/...',
        ios_url: application?.url_ios_shortened || application?.url_ios || 'https://apps.apple.com/us/app/...',
        // Legacy aliases
        firstName: 'Jane',
        application: appName,
        companyName: 'Mobex Health',
        email: 'jane.doe@example.com',
        password: 'TempPass123!',
    };
}
